import React, { useState, useEffect } from 'react';

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installState, setInstallState] = useState('hidden'); 
  // States: 'hidden', 'checking', 'ready', 'installing', 'success', 'ios_ready', 'manual_android'

  useEffect(() => {
    // 1. Check URL for ?install=true
    const urlParams = new URLSearchParams(window.location.search);
    const wantsToInstall = urlParams.get('install') === 'true';

    if (!wantsToInstall) return; 

    // 2. Are we ALREADY inside the installed app?
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;

    if (isInstalled) {
      window.history.replaceState(null, '', window.location.pathname); 
      setInstallState('hidden');
      return;
    }

    // 3. Detect Device Type
    const checkIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // 4. Handle iOS vs Android
    if (checkIOS) {
      setInstallState('ios_ready');
    } else {
      setInstallState('checking'); 

      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        window.deferredPrompt = e; // Sync globally just in case
        setDeferredPrompt(e);
        setInstallState('ready'); 
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      // 🚀 NEW: Actively scan for the prompt multiple times a second in case Chrome is slow
      const promptInterval = setInterval(() => {
        if (window.deferredPrompt) {
          setDeferredPrompt(window.deferredPrompt);
          setInstallState('ready');
          clearInterval(promptInterval); 
        }
      }, 300);

      // Fallback: If Chrome doesn't provide the prompt after 3 seconds, 
      // bypass the broken button and show the manual instructions directly.
      const fallbackTimeout = setTimeout(() => {
        setInstallState((prev) => prev === 'checking' ? 'manual_android' : prev);
      }, 3000);

      const handleAppInstalled = () => {
        setInstallState('success'); 
        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname);
          setInstallState('hidden');
        }, 2500);
      };

      window.addEventListener('appinstalled', handleAppInstalled);

      return () => {
        clearInterval(promptInterval);
        clearTimeout(fallbackTimeout);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }
  }, []);

  const handleInstallClick = async () => {
    const promptToUse = deferredPrompt || window.deferredPrompt;

    if (!promptToUse) {
      // The browser definitively blocked the prompt (Missing PNGs, or Chrome cooldown block)
      setInstallState('manual_android');
      return;
    }

    setInstallState('installing'); 
    promptToUse.prompt();

    const { outcome } = await promptToUse.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      window.deferredPrompt = null;
    } else {
      setInstallState('ready'); 
    }
  };

  const handleCancelClick = () => {
    window.history.replaceState(null, '', window.location.pathname);
    setInstallState('hidden');
  };

  // ================= UI RENDERING =================

  if (installState === 'hidden') return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: '#FFFFFF', zIndex: 10000, display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      
      {installState === 'checking' && (
        <div style={{ textAlign: 'center' }}>
          <div className="splash-loader" style={{ margin: '0 auto 20px auto' }}></div>
          <p style={{ color: '#6E655E', fontWeight: '600' }}>Preparing your experience...</p>
        </div>
      )}

      {/* iOS Instruction Screen */}
      {installState === 'ios_ready' && (
        <div style={{ textAlign: 'center', width: '85%', maxWidth: '320px' }}>
          <img src="/App-icon.svg" alt="Mealno" style={{ width: '80px', height: '80px', borderRadius: '18px', margin: '0 auto 20px auto', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }} />
          <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#3A2C23', margin: '0 0 8px 0', letterSpacing: '-0.5px' }}>Install Mealno</h2>
          
          <div style={{ background: '#FFF9F2', padding: '16px', borderRadius: '12px', marginBottom: '24px', textAlign: 'left', border: '1px solid #F8D548' }}>
            <p style={{ fontSize: '15px', color: '#6E655E', margin: '0 0 10px 0', fontWeight: 'bold' }}>To install on iOS:</p>
            <p style={{ fontSize: '14px', color: '#6E655E', margin: '0 0 8px 0' }}>1. Tap the <b>Share</b> icon at the bottom of Safari.</p>
            <p style={{ fontSize: '14px', color: '#6E655E', margin: '0' }}>2. Scroll down and select <b>Add to Home Screen</b>.</p>
          </div>
          
          <button 
            onClick={handleCancelClick}
            style={{ background: 'transparent', color: '#A59D96', border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer', padding: '10px', width: '100%' }}
          >
            Continue to Website
          </button>
        </div>
      )}

      {/* Android Manual Fallback */}
      {installState === 'manual_android' && (
        <div style={{ textAlign: 'center', width: '85%', maxWidth: '320px' }}>
          <img src="/App-icon.svg" alt="Mealno" style={{ width: '80px', height: '80px', borderRadius: '18px', margin: '0 auto 20px auto', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }} />
          <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#3A2C23', margin: '0 0 8px 0', letterSpacing: '-0.5px' }}>Almost there!</h2>
          
          <div style={{ background: '#FFF9F2', padding: '16px', borderRadius: '12px', marginBottom: '24px', textAlign: 'left', border: '1px solid #F8D548' }}>
            <p style={{ fontSize: '14px', color: '#6E655E', margin: '0 0 10px 0' }}>Your browser is blocking the automatic prompt, but you can still install it:</p>
            <p style={{ fontSize: '14px', color: '#6E655E', margin: '0 0 8px 0' }}>1. Tap the <b>three dots (⋮)</b> in the top right corner.</p>
            <p style={{ fontSize: '14px', color: '#6E655E', margin: '0' }}>2. Select <b>Install app</b> or <b>Add to Home screen</b>.</p>
          </div>
          
          <button 
            onClick={handleCancelClick}
            style={{ background: 'transparent', color: '#A59D96', border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer', padding: '10px', width: '100%' }}
          >
            Continue to Website
          </button>
        </div>
      )}

      {/* Android Ready State */}
      {installState === 'ready' && (
        <div style={{ textAlign: 'center', width: '85%', maxWidth: '320px' }}>
          <img src="/App-icon.svg" alt="Mealno" style={{ width: '80px', height: '80px', borderRadius: '18px', margin: '0 auto 20px auto', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }} />
          <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#3A2C23', margin: '0 0 8px 0', letterSpacing: '-0.5px' }}>Install Mealno</h2>
          <p style={{ fontSize: '15px', color: '#6E655E', margin: '0 0 30px 0', lineHeight: '1.4' }}>Get the seamless app experience for fast, zero-delivery fee orders.</p>
          
          <button 
            onClick={handleInstallClick}
            style={{
              width: '100%', padding: '16px', borderRadius: '12px', background: '#F8D548',
              color: '#3A2C23', fontWeight: 'bold', fontSize: '16px', border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(248, 213, 72, 0.4)', margin: '0 0 12px 0'
            }}
          >
            Install App
          </button>
          
          <button 
            onClick={handleCancelClick}
            style={{ background: 'transparent', color: '#A59D96', border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer', padding: '10px', width: '100%' }}
          >
            Continue to Website
          </button>
        </div>
      )}

      {/* Android Installing Spinner */}
      {installState === 'installing' && (
        <div style={{ textAlign: 'center', width: '85%', maxWidth: '320px' }}>
          <img src="/App-icon.svg" alt="Mealno" style={{ width: '80px', height: '80px', borderRadius: '18px', margin: '0 auto 20px auto', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }} />
          <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#3A2C23', margin: '0 0 8px 0', letterSpacing: '-0.5px' }}>Install Mealno</h2>
          <p style={{ fontSize: '15px', color: '#6E655E', margin: '0 0 30px 0', lineHeight: '1.4' }}>Get the seamless app experience for fast, zero-delivery fee orders.</p>

           <button 
            disabled
            style={{
              width: '100%', padding: '16px', borderRadius: '12px', background: '#F8D548',
              color: '#3A2C23', fontWeight: 'bold', fontSize: '16px', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              margin: '0 0 12px 0'
            }}
          >
            <div className="splash-loader" style={{ width: '20px', height: '20px', borderWidth: '3px', borderTopColor: '#3A2C23', borderColor: 'rgba(58, 44, 35, 0.1)' }}></div>
            Installing...
          </button>
          <p style={{ color: '#6E655E', margin: '16px 0 0 0', fontSize: '14px' }}>Takes a few seconds</p>
        </div>
      )}

      {/* Android Success */}
      {installState === 'success' && (
        <div style={{ textAlign: 'center' }}>
          <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 20px auto', display: 'block' }}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <h2 style={{ color: '#3A2C23', margin: '0 0 10px 0', fontSize: '24px', fontWeight: '900' }}>App Installed!</h2>
          <p style={{ color: '#6E655E', margin: 0, fontSize: '15px' }}>Redirecting to Mealno...</p>
        </div>
      )}

    </div>
  );
}